/**
 * Shared helpers for menu-item option modifiers.
 *
 * Option group `values` may be either:
 *   - a plain string  (legacy, treated as +$0)
 *   - { label, price_delta }  (new, per-choice upcharge)
 *
 * `normalizeValue()` converts both shapes to a uniform object so consumers
 * (OptionPickerModal, CartContext, CartDrawer, admin editor) never have to
 * branch on the shape.
 */

export function normalizeValue(v) {
  if (typeof v === "string") return { label: v, price_delta: 0 };
  if (v && typeof v === "object") {
    return {
      label: String(v.label ?? ""),
      price_delta: Number(v.price_delta) || 0,
    };
  }
  return { label: "", price_delta: 0 };
}

export function valueLabels(group) {
  return (group?.values || []).map((v) => normalizeValue(v).label);
}

/** Look up the price delta for a chosen label within a single group. */
export function findValueDelta(group, label) {
  for (const v of group?.values || []) {
    const n = normalizeValue(v);
    if (n.label === label) return n.price_delta;
  }
  return 0;
}

/** Sum of deltas across every selected value in `selectedOptions`.
 *  Supports `included_when: {"Other Group": ["value_a", "value_b"]}` on a
 *  group — if any of those parent values are selected, this group's
 *  deltas are treated as 0 (the modifier is "included" in the parent
 *  choice's price). Mirrors the backend `_option_extras` logic so cart
 *  totals in-browser match what the server computes on submit. */
export function computeOptionExtras(menuItem, selectedOptions) {
  if (!selectedOptions || !menuItem?.options) return 0;
  const picksFor = (label) => {
    const raw = selectedOptions[label];
    if (raw == null) return new Set();
    if (Array.isArray(raw)) return new Set(raw.map(String));
    if (typeof raw === "string") return new Set([raw]);
    return new Set();
  };
  let extras = 0;
  for (const grp of menuItem.options) {
    const includedWhen = grp.included_when || {};
    let triggered = false;
    for (const [parentLabel, triggerValues] of Object.entries(includedWhen)) {
      const trig = new Set((triggerValues || []).map(String));
      const parent = picksFor(parentLabel);
      for (const v of parent) {
        if (trig.has(v)) { triggered = true; break; }
      }
      if (triggered) break;
    }
    if (triggered) continue;
    const picks = selectedOptions[grp.label] || [];
    const list = Array.isArray(picks) ? picks : [picks];
    for (const label of list) {
      extras += findValueDelta(grp, label);
    }
  }
  return Math.round(extras * 100) / 100;
}

/** True when a group's price deltas are neutralized by an `included_when`
 *  clause given the current selections. UI hint — lets the picker render
 *  "Included" instead of "+$5" next to each option. */
export function isGroupIncluded(group, selectedOptions) {
  const includedWhen = group?.included_when || {};
  const parents = Object.entries(includedWhen);
  if (parents.length === 0) return false;
  const picksFor = (label) => {
    const raw = selectedOptions?.[label];
    if (raw == null) return [];
    return Array.isArray(raw) ? raw.map(String) : [String(raw)];
  };
  for (const [parentLabel, triggerValues] of parents) {
    const trig = new Set((triggerValues || []).map(String));
    const parent = picksFor(parentLabel);
    for (const v of parent) if (trig.has(v)) return true;
  }
  return false;
}

/** True when a group must be picked given the current selections. Honors
 *  a `required_when: {"Other Group": ["value_a"]}` conditional in
 *  addition to the plain `required: true` flag. */
export function isGroupRequired(group, selectedOptions) {
  if (group?.required) return true;
  const requiredWhen = group?.required_when || {};
  const parents = Object.entries(requiredWhen);
  if (parents.length === 0) return false;
  const picksFor = (label) => {
    const raw = selectedOptions?.[label];
    if (raw == null) return [];
    return Array.isArray(raw) ? raw.map(String) : [String(raw)];
  };
  for (const [parentLabel, triggerValues] of parents) {
    const trig = new Set((triggerValues || []).map(String));
    const parent = picksFor(parentLabel);
    for (const v of parent) if (trig.has(v)) return true;
  }
  return false;
}

/** Per-unit price including selected option upcharges. */
export function unitPriceFor(menuItem, line) {
  if (!menuItem) return 0;
  const base = Number(menuItem.price || 0);
  return Math.round((base + computeOptionExtras(menuItem, line?.selected_options)) * 100) / 100;
}

/** Format a delta for display: 0 → "", 3 → "+$3", 3.5 → "+$3.50". */
export function formatDelta(delta) {
  const d = Number(delta) || 0;
  if (Math.abs(d) < 0.005) return "";
  const sign = d > 0 ? "+" : "−";
  const abs = Math.abs(d);
  const str = abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2);
  return `${sign}$${str}`;
}
